import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAdminUserById, toggleUserStatus, creditUserWallet, debitUserWallet, impersonateUser, updateAdminUserProfile } from '../../api/adminApi';
import { fmtIST } from '../../utils/dateFormat';

const cyan = '#00e5ff';

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
};

const WALLET_LABELS = { main_wallet: 'Main Wallet', earning_wallet: 'Earning Wallet', one_wallet: 'One Wallet', reward_wallet: 'Reward Wallet' };
const WALLET_COLORS = { main_wallet: '#66aaff', earning_wallet: '#66ff99', one_wallet: '#ff88cc', reward_wallet: '#ffaa44' };

const AdminUserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  const isMobile = useIsMobile();

  // Credit modal
  const [showCredit, setShowCredit] = useState(false);
  const [creditForm, setCreditForm] = useState({ walletType: 'main_wallet', amount: '', comment: '' });
  const [crediting, setCrediting] = useState(false);
  const [creditMsg, setCreditMsg] = useState('');

  // Edit profile modal
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editing, setEditing] = useState(false);
  const [editMsg, setEditMsg] = useState('');

  // Debit modal
  const [showDebit, setShowDebit] = useState(false);
  const [debitForm, setDebitForm] = useState({ walletType: 'main_wallet', amount: '', comment: '' });
  const [debiting, setDebiting] = useState(false);
  const [debitMsg, setDebitMsg] = useState('');

  const load = () => {
    setLoading(true);
    getAdminUserById(id).then((res) => setData(res.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const handleToggle = async () => {
    setToggling(true);
    try {
      const res = await toggleUserStatus(id);
      setData((d) => ({ ...d, user: { ...d.user, isActive: res.data.isActive } }));
    } finally { setToggling(false); }
  };

  const handleImpersonate = async () => {
    setImpersonating(true);
    try {
      const res = await impersonateUser(id);
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('impersonating', JSON.stringify({ name: res.data.user.name, userCode: res.data.user.userCode }));
      window.location.href = '/dashboard';
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to impersonate');
      setImpersonating(false);
    }
  };

  const openEdit = () => {
    setEditForm({
      name: user.name || '',
      email: user.email || '',
      mobile: user.mobile || '',
      country: user.country || '',
      usdtWalletAddress: user.usdtWalletAddress || '',
      address: user.address || '',
      dateOfBirth: user.dateOfBirth ? user.dateOfBirth.slice(0, 10) : '',
      withdrawalLimit: user.withdrawalLimit ?? 0,
    });
    setEditMsg('');
    setShowEdit(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setEditing(true);
    setEditMsg('');
    try {
      const res = await updateAdminUserProfile(id, editForm);
      setData((d) => ({ ...d, user: { ...d.user, ...res.data.user } }));
      setEditMsg('✓ Profile updated successfully');
      setTimeout(() => setShowEdit(false), 1200);
    } catch (err) {
      setEditMsg(err.response?.data?.message || 'Update failed');
    } finally { setEditing(false); }
  };

  const handleCredit = async (e) => {
    e.preventDefault();
    setCrediting(true);
    setCreditMsg('');
    try {
      await creditUserWallet(id, creditForm);
      setCreditMsg('✓ Credited successfully');
      load();
      setTimeout(() => { setShowCredit(false); setCreditMsg(''); setCreditForm({ walletType: 'main_wallet', amount: '', comment: '' }); }, 1500);
    } catch (err) {
      setCreditMsg(err.response?.data?.message || 'Credit failed');
    } finally { setCrediting(false); }
  };

  const handleDebit = async (e) => {
    e.preventDefault();
    setDebiting(true);
    setDebitMsg('');
    try {
      await debitUserWallet(id, debitForm);
      setDebitMsg('✓ Debited successfully');
      load();
      setTimeout(() => { setShowDebit(false); setDebitMsg(''); setDebitForm({ walletType: 'main_wallet', amount: '', comment: '' }); }, 1500);
    } catch (err) {
      setDebitMsg(err.response?.data?.message || 'Debit failed');
    } finally { setDebiting(false); }
  };

  if (loading) return <div style={{ color: '#555', fontSize: 13 }}>Loading...</div>;
  if (!data) return <div style={{ color: '#ff6666', fontSize: 13 }}>User not found.</div>;

  const { user, wallets, slots, directCount, txns } = data;
  const isActive = user.isActive;

  return (
    <div>
      <button onClick={() => navigate('/admin/users')} style={{ background: 'none', border: 'none', color: cyan, fontSize: 13, cursor: 'pointer', marginBottom: 20, padding: 0 }}>
        ← Back to Users
      </button>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
        <div>
          <h4 style={{ color: '#fff', fontWeight: 700, marginBottom: 2 }}>{user.name}</h4>
          <div style={{ color: '#555', fontSize: 13 }}>{user.email} · {user.userCode}</div>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button onClick={openEdit} style={{ background: 'rgba(255,200,0,0.1)', border: '1px solid rgba(255,200,0,0.35)', color: '#ffc800', borderRadius: 8, padding: '8px 18px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            Edit Profile
          </button>
          <button onClick={handleImpersonate} disabled={impersonating} style={{ background: 'rgba(0,229,255,0.08)', border: '1px solid rgba(0,229,255,0.3)', color: cyan, borderRadius: 8, padding: '8px 18px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            {impersonating ? '...' : 'Login as User'}
          </button>
          <button onClick={() => setShowCredit(true)} style={{ background: cyan, border: 'none', color: '#000', borderRadius: 8, padding: '8px 18px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            + Manual Credit
          </button>
          <button onClick={() => setShowDebit(true)} style={{ background: 'rgba(200,50,50,0.15)', border: '1px solid rgba(200,50,50,0.4)', color: '#ff6666', borderRadius: 8, padding: '8px 18px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            − Manual Debit
          </button>
          <button onClick={handleToggle} disabled={toggling} style={{ background: isActive ? 'rgba(200,50,50,0.15)' : 'rgba(102,255,153,0.15)', border: `1px solid ${isActive ? 'rgba(200,50,50,0.4)' : 'rgba(102,255,153,0.4)'}`, color: isActive ? '#ff6666' : '#66ff99', borderRadius: 8, padding: '8px 18px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            {toggling ? '...' : isActive ? 'Deactivate' : 'Activate'}
          </button>
        </div>
      </div>

      {/* Info + Wallets grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        {/* User Info */}
        <div style={{ background: '#111', border: '1px solid rgba(0,229,255,0.1)', borderRadius: 12, padding: '18px 20px' }}>
          <div style={{ color: '#aaa', fontSize: 12, fontWeight: 600, marginBottom: 14 }}>Profile</div>
          {[
            ['Mobile', user.mobile],
            ['Country', user.country || '—'],
            ['USDT Address', user.usdtAddress || '—'],
            ['Referred By', user.referredBy ? `${user.referredBy.name} (${user.referredBy.userCode})` : '—'],
            ['Direct Referrals', directCount],
            ['Active Slot', user.planId?.planName || '—'],
            ['Withdrawal Limit', `${(user.withdrawalLimit || 0).toFixed(2)} USDT`],
            ['Status', isActive ? '✓ Active' : '✗ Inactive'],
            ['Joined', fmtIST(user.createdAt)],
          ].map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', gap: 12 }}>
              <span style={{ color: '#555', fontSize: 12 }}>{k}</span>
              <span style={{ color: '#ccc', fontSize: 12, fontWeight: 500, textAlign: 'right', wordBreak: 'break-all' }}>{String(v)}</span>
            </div>
          ))}
        </div>

        {/* Wallets */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {wallets.map((w) => (
            <div key={w._id} style={{ background: '#111', border: '1px solid rgba(0,229,255,0.1)', borderRadius: 12, padding: '14px 18px' }}>
              <div style={{ color: '#555', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
                {WALLET_LABELS[w.walletType] || w.walletType}
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: WALLET_COLORS[w.walletType] || '#fff' }}>
                {w.balance.toFixed(2)} <span style={{ fontSize: 12, color: '#555', fontWeight: 400 }}>USDT</span>
              </div>
            </div>
          ))}

          {/* Purchased Slots */}
          <div style={{ background: '#111', border: '1px solid rgba(0,229,255,0.1)', borderRadius: 12, padding: '14px 18px' }}>
            <div style={{ color: '#555', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Upgraded Slots</div>
            {slots.length === 0 ? (
              <div style={{ color: '#444', fontSize: 12 }}>No slots yet</div>
            ) : slots.map((s) => (
              <div key={s._id} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ color: '#fff', fontSize: 13 }}>{s.planId?.planName}</span>
                <span style={{ color: '#66ff99', fontSize: 12 }}>{s.planId?.amount} USDT</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Transactions */}
      <div style={{ background: '#111', border: '1px solid rgba(0,229,255,0.1)', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid rgba(0,229,255,0.08)', color: '#aaa', fontSize: 13, fontWeight: 600 }}>
          Recent Transactions
        </div>
        <div style={{ overflow: isMobile ? 'auto' : 'hidden', overflowY: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? '500px' : '100%' }}>
          <thead>
            <tr style={{ background: 'rgba(0,229,255,0.04)' }}>
              {['Txn ID', 'Wallet', 'Type', 'Amount', 'Comments', 'Date'].map((h) => (
                <th key={h} style={{ color: cyan, fontSize: 11, fontWeight: 600, padding: '10px 14px', textAlign: 'left' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {txns.length === 0 ? (
              <tr><td colSpan={6} style={{ color: '#444', textAlign: 'center', padding: 24, fontSize: 12 }}>No transactions.</td></tr>
            ) : txns.map((t) => (
              <tr key={t._id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <td style={{ color: '#444', fontSize: 11, padding: '9px 14px', fontFamily: 'monospace' }}>{t._id.slice(-10).toUpperCase()}</td>
                <td style={{ color: '#888', fontSize: 11, padding: '9px 14px' }}>{WALLET_LABELS[t.walletId?.walletType] || '—'}</td>
                <td style={{ padding: '9px 14px' }}>
                  <span style={{ background: t.txnType === 'credit' ? 'rgba(102,255,153,0.12)' : 'rgba(200,50,50,0.12)', color: t.txnType === 'credit' ? '#66ff99' : '#ff6666', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                    {t.txnType}
                  </span>
                </td>
                <td style={{ color: t.txnType === 'credit' ? '#66ff99' : '#ff6666', fontWeight: 600, fontSize: 13, padding: '9px 14px' }}>
                  {t.txnType === 'credit' ? '+' : '−'}{t.amount.toFixed(2)}
                </td>
                <td style={{ color: '#666', fontSize: 11, padding: '9px 14px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.comments || '—'}</td>
                <td style={{ color: '#444', fontSize: 11, padding: '9px 14px', whiteSpace: 'nowrap' }}>{fmtIST(t.createdAt)}</td>
              </tr>
            ))}
          </tbody>
          </table>
        </div>
      </div>

      {/* Edit Profile Modal */}
      {showEdit && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999, padding: '20px' }}>
          <div style={{ background: '#111', border: '1px solid rgba(255,200,0,0.3)', borderRadius: 14, padding: '28px', width: '100%', maxWidth: 500, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 16, marginBottom: 20 }}>Edit Profile — {user.name}</div>
            <form onSubmit={handleEdit}>
              {[
                { label: 'Full Name', field: 'name', type: 'text', required: true },
                { label: 'Email', field: 'email', type: 'email', required: true },
                { label: 'Mobile', field: 'mobile', type: 'text', required: true },
                { label: 'Country', field: 'country', type: 'text' },
                { label: 'USDT Wallet Address', field: 'usdtWalletAddress', type: 'text' },
                { label: 'Address', field: 'address', type: 'text' },
                { label: 'Date of Birth', field: 'dateOfBirth', type: 'date' },
                { label: 'Withdrawal Limit (USDT)', field: 'withdrawalLimit', type: 'number' },
              ].map(({ label, field, type, required }) => (
                <div key={field} style={{ marginBottom: 14 }}>
                  <label style={{ color: '#888', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>{label}</label>
                  <input
                    type={type}
                    value={editForm[field]}
                    onChange={(e) => setEditForm({ ...editForm, [field]: e.target.value })}
                    required={required}
                    min={field === 'withdrawalLimit' ? 0 : undefined}
                    step={field === 'withdrawalLimit' ? 'any' : undefined}
                    style={{ width: '100%', background: 'rgba(255,200,0,0.04)', border: '1px solid rgba(255,200,0,0.2)', borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                  />
                </div>
              ))}
              {editMsg && (
                <div style={{ color: editMsg.startsWith('✓') ? '#66ff99' : '#ff6666', fontSize: 13, marginBottom: 14 }}>{editMsg}</div>
              )}
              <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                <button type="button" onClick={() => setShowEdit(false)}
                  style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: '#888', borderRadius: 8, cursor: 'pointer' }}>
                  Cancel
                </button>
                <button type="submit" disabled={editing}
                  style={{ flex: 1, padding: '10px', background: editing ? 'rgba(255,255,255,0.06)' : 'rgba(255,200,0,0.15)', border: '1px solid rgba(255,200,0,0.4)', color: editing ? '#555' : '#ffc800', borderRadius: 8, fontWeight: 700, cursor: editing ? 'not-allowed' : 'pointer' }}>
                  {editing ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manual Debit Modal */}
      {showDebit && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
          <div style={{ background: '#111', border: '1px solid rgba(200,50,50,0.3)', borderRadius: 14, padding: '28px 28px', width: '100%', maxWidth: 400 }}>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 16, marginBottom: 20 }}>Manual Debit — {user.name}</div>
            <form onSubmit={handleDebit}>
              <div style={{ marginBottom: 14 }}>
                <label style={{ color: '#888', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Wallet</label>
                <select value={debitForm.walletType} onChange={(e) => setDebitForm({ ...debitForm, walletType: e.target.value })}
                  style={{ width: '100%', background: '#1a1a1a', border: '1px solid rgba(200,50,50,0.3)', borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 13, outline: 'none' }}>
                  <option value="main_wallet">Main Wallet</option>
                  <option value="earning_wallet">Earning Wallet</option>
                  <option value="one_wallet">One Wallet</option>
                </select>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ color: '#888', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Amount (USDT)</label>
                <input type="number" min="0.01" step="any" placeholder="0.00" value={debitForm.amount}
                  onChange={(e) => setDebitForm({ ...debitForm, amount: e.target.value })}
                  style={{ width: '100%', background: 'rgba(200,50,50,0.05)', border: '1px solid rgba(200,50,50,0.3)', borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 14, outline: 'none' }}
                  required />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ color: '#888', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Comment</label>
                <input type="text" placeholder="Reason for debit..." value={debitForm.comment}
                  onChange={(e) => setDebitForm({ ...debitForm, comment: e.target.value })}
                  style={{ width: '100%', background: 'rgba(200,50,50,0.05)', border: '1px solid rgba(200,50,50,0.3)', borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 14, outline: 'none' }} />
              </div>
              {debitMsg && (
                <div style={{ color: debitMsg.startsWith('✓') ? '#66ff99' : '#ff6666', fontSize: 13, marginBottom: 14 }}>{debitMsg}</div>
              )}
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" onClick={() => { setShowDebit(false); setDebitMsg(''); }}
                  style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: '#888', borderRadius: 8, cursor: 'pointer' }}>
                  Cancel
                </button>
                <button type="submit" disabled={debiting}
                  style={{ flex: 1, padding: '10px', background: debiting ? 'rgba(255,255,255,0.06)' : 'rgba(200,50,50,0.2)', border: '1px solid rgba(200,50,50,0.4)', color: debiting ? '#555' : '#ff6666', borderRadius: 8, fontWeight: 700, cursor: debiting ? 'not-allowed' : 'pointer' }}>
                  {debiting ? 'Processing...' : 'Debit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manual Credit Modal */}
      {showCredit && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
          <div style={{ background: '#111', border: '1px solid rgba(0,229,255,0.2)', borderRadius: 14, padding: '28px 28px', width: '100%', maxWidth: 400 }}>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 16, marginBottom: 20 }}>Manual Credit — {user.name}</div>
            <form onSubmit={handleCredit}>
              <div style={{ marginBottom: 14 }}>
                <label style={{ color: '#888', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Wallet</label>
                <select value={creditForm.walletType} onChange={(e) => setCreditForm({ ...creditForm, walletType: e.target.value })}
                  style={{ width: '100%', background: '#1a1a1a', border: '1px solid rgba(0,229,255,0.2)', borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 13, outline: 'none' }}>
                  <option value="main_wallet">Main Wallet</option>
                  <option value="earning_wallet">Earning Wallet</option>
                  <option value="one_wallet">One Wallet</option>
                </select>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ color: '#888', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Amount (USDT)</label>
                <input type="number" min="0.01" step="any" placeholder="0.00" value={creditForm.amount}
                  onChange={(e) => setCreditForm({ ...creditForm, amount: e.target.value })}
                  style={{ width: '100%', background: 'rgba(0,229,255,0.04)', border: '1px solid rgba(0,229,255,0.2)', borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 14, outline: 'none' }}
                  required />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ color: '#888', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Comment</label>
                <input type="text" placeholder="Reason for credit..." value={creditForm.comment}
                  onChange={(e) => setCreditForm({ ...creditForm, comment: e.target.value })}
                  style={{ width: '100%', background: 'rgba(0,229,255,0.04)', border: '1px solid rgba(0,229,255,0.2)', borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 14, outline: 'none' }} />
              </div>
              {creditMsg && (
                <div style={{ color: creditMsg.startsWith('✓') ? '#66ff99' : '#ff6666', fontSize: 13, marginBottom: 14 }}>{creditMsg}</div>
              )}
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" onClick={() => { setShowCredit(false); setCreditMsg(''); }}
                  style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: '#888', borderRadius: 8, cursor: 'pointer' }}>
                  Cancel
                </button>
                <button type="submit" disabled={crediting}
                  style={{ flex: 1, padding: '10px', background: crediting ? 'rgba(255,255,255,0.06)' : cyan, border: 'none', color: crediting ? '#555' : '#000', borderRadius: 8, fontWeight: 700, cursor: crediting ? 'not-allowed' : 'pointer' }}>
                  {crediting ? 'Processing...' : 'Credit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserDetail;
